import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getMarketingLocale } from '../lib/marketingLocale';
import { getConsentRecord, setConsent, isAppPath } from '../lib/analytics';
import { onOpenCookieSettings } from '../lib/consentSettings';
import { CMP_NOTICE } from '../lib/cmpNotice';

// Source unique du défaut « Analytics » (catégorie non-essentielle) : le
// même `CMP_NOTICE` que celui référencé par `lib/analytics.js` pour la
// version stockée dans chaque enregistrement de consentement — la valeur
// rendue ici et la version persistée proviennent donc toujours du même objet.
const ANALYTICS_DEFAULT = CMP_NOTICE.categories.analytics.default;

// Parité visuelle Accept/Reject (pré-push patch) — mêmes taille/poids/traitement
// de bouton pour « Accepter tout » et « Refuser tout », dans le bandeau ET le
// panneau Personnaliser : seule la couleur distingue les deux actions, aucune
// des deux n'est en retrait visuel par rapport à l'autre. « Personnaliser »
// reste volontairement secondaire (bouton fantôme).
const ACCEPT_BTN = 'px-5 py-2.5 rounded-full text-[14px] font-semibold text-white bg-[#2F5D46] hover:bg-[#2F5D46]/90 transition-colors';
const REJECT_BTN = 'px-5 py-2.5 rounded-full text-[14px] font-semibold text-white bg-[#1E1E1E] hover:bg-[#1E1E1E]/90 transition-colors';
const GHOST_BTN = 'px-4 py-2.5 rounded-full text-[14px] font-medium text-[#1E1E1E]/70 hover:bg-[#1E1E1E]/5 transition-colors';

/**
 * CMP V1 — Bandeau + panneau « Customize » de consentement RGPD (opt-in strict).
 *
 * Aucun outil de mesure d'audience (GA4) ne doit être activé tant que
 * l'utilisateur n'a pas cliqué « Accepter tout » ou explicitement activé
 * « Analytics » dans le panneau Personnaliser puis « Enregistrer ». Le choix
 * est un enregistrement structuré et versionné (voir `lib/analytics.js`,
 * `setConsent`/`getConsentRecord`) : `{ version, analytics, timestamp, source }`.
 *
 * Réouverture : le lien persistant « Cookie Settings » (footer marketing /
 * pages légales) déclenche `openCookieSettings()` (`lib/consentSettings.js`),
 * auquel ce composant est abonné — aucun besoin de vider le localStorage
 * pour revenir sur son choix.
 *
 * `/app/*` — GA4 y est de toute façon exclu (`isAppPath`, `lib/analytics.js`) ;
 * le bandeau initial ne s'y affiche donc pas spontanément (CMP V1 §10), mais
 * reste ouvrable via « Cookie Settings » si le lien y est un jour exposé.
 */

const COPY = {
  fr: {
    title: 'Votre vie privée',
    body: 'Nous utilisons des cookies de mesure d’audience pour comprendre l’usage du site et l’améliorer. Aucun outil de suivi n’est activé sans votre accord.',
    privacy: 'Politique de confidentialité',
    acceptAll: 'Accepter tout',
    rejectAll: 'Refuser tout',
    customize: 'Personnaliser',
    customizeTitle: 'Préférences de cookies',
    necessaryTitle: 'Strictement nécessaires',
    necessaryBody: 'Indispensables au fonctionnement du site (authentification, panier, langue, sécurité). Toujours actifs.',
    analyticsTitle: 'Analytics',
    analyticsBody: 'Google Analytics — mesure d’audience pour comprendre l’usage du site public et l’améliorer. Désactivé par défaut.',
    save: 'Enregistrer les choix',
  },
  en: {
    title: 'Your privacy',
    body: 'We use audience-measurement cookies to understand how the site is used and improve it. No tracking tool is activated without your consent.',
    privacy: 'Privacy policy',
    acceptAll: 'Accept all',
    rejectAll: 'Reject all',
    customize: 'Customize',
    customizeTitle: 'Cookie preferences',
    necessaryTitle: 'Strictly necessary',
    necessaryBody: 'Required for the site to function (authentication, cart, language, security). Always active.',
    analyticsTitle: 'Analytics',
    analyticsBody: 'Google Analytics — audience measurement to understand public-site usage and improve it. Off by default.',
    save: 'Save choices',
  },
};

export default function CookieConsent() {
  const { language } = useLanguage();
  const location = useLocation();
  // Sur les pages marketing EN (/en/*), la copie suit la locale marketing.
  const effectiveLang = getMarketingLocale(location.pathname) === 'en' ? 'en' : language;
  const c = COPY[effectiveLang] || COPY.fr;

  const [record, setRecord] = useState(
    () => (typeof window !== 'undefined' ? getConsentRecord() : null)
  );
  const [open, setOpen] = useState(() => record === null);
  const [mode, setMode] = useState('banner'); // 'banner' | 'customize'
  const [pendingAnalytics, setPendingAnalytics] = useState(() => (record ? record.analytics : ANALYTICS_DEFAULT));
  const [manuallyOpened, setManuallyOpened] = useState(false);

  useEffect(
    () => onOpenCookieSettings(() => {
      const current = getConsentRecord();
      setPendingAnalytics(current ? current.analytics : ANALYTICS_DEFAULT);
      setMode('customize');
      setManuallyOpened(true);
      setOpen(true);
    }),
    []
  );

  const persist = (analyticsChoice) => {
    const source = manuallyOpened ? 'settings' : 'banner';
    const nextRecord = setConsent({ analytics: analyticsChoice, source });
    setRecord(nextRecord);
    setOpen(false);
    setMode('banner');
    setManuallyOpened(false);
  };

  const acceptAll = () => persist(true);
  const rejectAll = () => persist(false);
  const saveChoices = () => persist(pendingAnalytics);
  const openCustomize = () => {
    setPendingAnalytics(record ? record.analytics : ANALYTICS_DEFAULT);
    setMode('customize');
  };

  if (!open) return null;
  // CMP V1 §10 — pas de bandeau initial sur les routes authentifiées /app/*
  // (aucun tracking non-essentiel ne peut s'y exécuter) ; reste ouvrable via
  // le lien « Cookie Settings » si celui-ci y est un jour exposé.
  if (isAppPath(location.pathname) && !manuallyOpened) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[120] p-3 sm:p-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pointer-events-none"
      role="dialog"
      aria-live="polite"
      aria-label={mode === 'customize' ? c.customizeTitle : c.title}
      data-testid="cookie-consent-banner"
    >
      <div className="mx-auto max-w-3xl rounded-2xl border border-[#E8E5DE] bg-white/95 backdrop-blur-md shadow-[0_8px_30px_rgba(30,30,30,0.12)] p-4 sm:p-5 pointer-events-auto">
        {mode === 'banner' ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-semibold text-[#1E1E1E]" data-testid="cookie-consent-title">
                {c.title}
              </p>
              <p className="mt-1 text-[13.5px] leading-relaxed text-[#1E1E1E]/70">
                {c.body}{' '}
                <Link
                  to="/privacy"
                  className="text-[#2F5D46] font-medium underline underline-offset-2 hover:text-[#2F5D46]/80"
                  data-testid="cookie-consent-privacy-link"
                >
                  {c.privacy}
                </Link>
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={openCustomize}
                className={GHOST_BTN}
                data-testid="cookie-consent-customize"
              >
                {c.customize}
              </button>
              <button
                type="button"
                onClick={rejectAll}
                className={REJECT_BTN}
                data-testid="cookie-consent-refuse"
              >
                {c.rejectAll}
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className={ACCEPT_BTN}
                data-testid="cookie-consent-accept"
              >
                {c.acceptAll}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-[15px] font-semibold text-[#1E1E1E]" data-testid="cookie-consent-customize-title">
              {c.customizeTitle}
            </p>

            <div className="mt-4 space-y-3">
              <div className="flex items-start justify-between gap-4 rounded-xl border border-[#E8E5DE] p-3" data-testid="cookie-consent-category-necessary">
                <div className="min-w-0">
                  <p className="text-[13.5px] font-semibold text-[#1E1E1E]">{c.necessaryTitle}</p>
                  <p className="mt-0.5 text-[12.5px] leading-relaxed text-[#1E1E1E]/60">{c.necessaryBody}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked="true"
                  disabled
                  aria-disabled="true"
                  className="shrink-0 w-10 h-6 rounded-full bg-[#2F5D46] opacity-60 cursor-not-allowed relative"
                  data-testid="cookie-consent-toggle-necessary"
                >
                  <span className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-white" />
                </button>
              </div>

              <div className="flex items-start justify-between gap-4 rounded-xl border border-[#E8E5DE] p-3" data-testid="cookie-consent-category-analytics">
                <div className="min-w-0">
                  <p className="text-[13.5px] font-semibold text-[#1E1E1E]">{c.analyticsTitle}</p>
                  <p className="mt-0.5 text-[12.5px] leading-relaxed text-[#1E1E1E]/60">{c.analyticsBody}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={pendingAnalytics}
                  onClick={() => setPendingAnalytics((v) => !v)}
                  className={`shrink-0 w-10 h-6 rounded-full relative transition-colors ${pendingAnalytics ? 'bg-[#2F5D46]' : 'bg-[#1E1E1E]/20'}`}
                  data-testid="cookie-consent-toggle-analytics"
                >
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${pendingAnalytics ? 'right-0.5' : 'left-0.5'}`} />
                </button>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={rejectAll}
                className={REJECT_BTN}
                data-testid="cookie-consent-reject-all-customize"
              >
                {c.rejectAll}
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className={ACCEPT_BTN}
                data-testid="cookie-consent-accept-all-customize"
              >
                {c.acceptAll}
              </button>
              <button
                type="button"
                onClick={saveChoices}
                className={ACCEPT_BTN}
                data-testid="cookie-consent-save"
              >
                {c.save}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
